import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-react";
import React from "react";

class TextEditor extends React.Component<any, any> {
  editorInstance: any = null;

  constructor(props: any) {
    super(props);
    this.state = {
      selectedText: "",
    };
  }

  /* ------------------------ LIFECYCLE ------------------------ */

  componentDidUpdate(prevProps: any) {
    if (prevProps.bgColor !== this.props.bgColor) {
      this.updateEditorBackground(this.props.bgColor);
    }
  }

  componentWillUnmount() {
    this.removeLinkInsertionListener();
  }

  /* ------------------------ LISTENERS ------------------------ */

  setupLinkInsertionListener = () => {
    window.addEventListener("insertLinkToEditor", this.handleInsertLink);
  };

  removeLinkInsertionListener = () => {
    window.removeEventListener("insertLinkToEditor", this.handleInsertLink);
  };

  /* ------------------------ INIT ------------------------ */

  handleEditorReady = (editor: any) => {
    this.editorInstance = editor;

    this.setupLinkInsertionListener();

    // Detect selection
    editor.model.document.selection.on("change:range", () => {
      this.detectSelectedText(editor);
    });

    // Apply initial background
    setTimeout(() => {
      if (this.props.bgColor) {
        this.updateEditorBackground(this.props.bgColor);
      }
    }, 100);
  };

  /* ------------------------ TEXT SELECTION ------------------------ */

  detectSelectedText = (editor: any) => {
    const selection = editor.model.document.selection;

    if (selection.isCollapsed) {
      this.setState({ selectedText: "" });
      return;
    }

    const text = this.getModelSelectedText(editor, selection);

    if (text?.trim()) {
      this.setState({ selectedText: text });
      this.props.onTextSelect?.(text);
    }
  };

  getModelSelectedText = (editor: any, selection: any) => {
    let text = "";
    const range = selection.getFirstRange();

    for (const item of range.getItems()) {
      if (item.is("$text") || item.is("$textProxy")) {
        text += item.data;
      }
    }

    return text;
  };

  /* ------------------------ LINK INSERTION ------------------------ */

  handleInsertLink = (event: any) => {
  const { text, link, page } = event.detail;
  if (!this.editorInstance) return;

  const href = `${link}#page=${page}`;

  this.editorInstance.model.change((writer: any) => {
    const selection = this.editorInstance.model.document.selection;

    if (!selection.isCollapsed) {
      // Replace the selected text fully with the link text
      const range = selection.getFirstRange();

      // Delete selected text
      writer.remove(range);

      // Insert the new linked text in same position
      const textNode = writer.createText(text, { linkHref: href });
      this.editorInstance.model.insertContent(textNode, range.start);

    } else {
      // Insert link at cursor
      const textNode = writer.createText(text, { linkHref: href });
      this.editorInstance.model.insertContent(textNode, selection);
    }
  });

  this.triggerChange();
};


  /**
   * SAFE METHOD — no circular JSON, no writer-level insertion
   */
  insertHtmlLink = (html: string) => {
    const editor = this.editorInstance;
    try {
      editor.data.insertContent(html);
      this.triggerChange();
    } catch (err) {
      console.error("Insert HTML link error:", err);
    }
  };

  insertSimpleLink = (text: string, link: string, page: number) => {
    const editor = this.editorInstance;

    editor.model.change((writer: any) => {
      const selection = editor.model.document.selection;
      const href = `${link}#page=${page}`;

      if (selection.isCollapsed) {
        const linkText = writer.createText(text, { linkHref: href });
        editor.model.insertContent(linkText, selection);
      } else {
        writer.setAttribute("linkHref", href, selection);
      }
    });

    this.triggerChange();
  };

  /* ------------------------ CHANGE PROPAGATION ------------------------ */

  triggerChange = () => {
    if (!this.props.onChange) return;

    setTimeout(() => {
      this.props.onChange({
        target: {
          name: this.props.name,
          value: this.editorInstance.getData(), // SAFE
        },
      });
    }, 20);
  };

  /* ------------------------ BACKGROUND COLOR ------------------------ */

  updateEditorBackground = (color: string | null) => {
    if (!this.editorInstance) return;

    try {
      this.editorInstance.editing.view.change((writer: any) => {
        const root = this.editorInstance.editing.view.document.getRoot();

        if (color) {
          writer.setStyle("background-color", color, root);
        } else {
          writer.removeStyle("background-color", root);
        }

        writer.setStyle("min-height", "180px", root);
      });
    } catch (err) {
      console.error("Background error:", err);
    }
  };

  /* ------------------------ RENDER ------------------------ */

  render() {
    const { error, name, value, placeholder } = this.props;

    const editorConfiguration = {
      placeholder,
      toolbar: [
        "heading",
        "|",
        "bold",
        "italic",
        "link",
        "numberedList",
        "bulletedList",
        "|",
        "insertTable",
        "tableColumn",
        "tableRow",
        "mergeTableCells",
      ],
      link: { addTargetToExternalLinks: true },
    };

    return (
      <div className={error ? "border border-danger" : ""}>
        <CKEditor
          editor={ClassicEditor}
          config={editorConfiguration}
          data={value}
          onInit={this.handleEditorReady}
          onChange={(event: any, editor: any) => {
            const html = editor.getData();
            this.props.onChange?.({
              target: { name, value: html },
            });
          }}
        />
      </div>
    );
  }
}

export default TextEditor;
